import { Suspense, useMemo } from 'react'
import { omitAll } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import { Header } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import {
	getLast90DaysPeriods,
	getMoMPeriods,
	getYoYPeriods,
} from '../../lib/helpers'
import {
	useDashboardStore,
	useSelectDashboardFilters,
	useSelectDashboardFiltersKeys,
} from '../../lib/store'
import BusinessMetricBoostsWidget from '../widgets/BusinessMetricBoosts/Widget'
import HistoricalPriceAcceptanceWidget from '../widgets/HistoricalPriceAcceptance/Widget'
import NumericAcceptedPriceWidget from '../widgets/NumericAcceptedPrice/Widget'
import NumericAcceptedRateWidget from '../widgets/NumericAcceptedRate/Widget'
import NumericBlockedForRepricingWidget from '../widgets/NumericBlockedForRepricing/Widget'
import NumericGrossProfitBoostWidget from '../widgets/NumericGrossProfitBoost/Widget'
import NumericOptimizedPriceWidget from '../widgets/NumericOptimizedPrice/Widget'
import NumericRevenueBoostWidget from '../widgets/NumericRevenueBoost/Widget'
import NumericRevenueUnderManagementWidget from '../widgets/NumericRevenueUnderManagement/Widget'
import PriceAcceptanceDynamicsWidget from '../widgets/PriceAcceptanceDynamics/Widget'
import { useFilterFieldsConfig } from './useFilterFiledsConfig'

export default function MainDashboardPricingHistoricalSection({
	biRevisionDate,
}: {
	biRevisionDate: string
}) {
	const { t } = useTranslation()

	const yearOverYearPeriods = useMemo(
		() => getYoYPeriods(biRevisionDate),
		[biRevisionDate]
	)
	const monthOverMonthPeriods = useMemo(
		() => getMoMPeriods(biRevisionDate),
		[biRevisionDate]
	)
	const last90daysPeriods = useMemo(() => getLast90DaysPeriods(), [])
	const { setFilters, filters } = useDashboardStore(
		useSelectDashboardFilters('main_pricing')
	)
	const { storedKeys, setStoredKeys } = useDashboardStore(
		useSelectDashboardFiltersKeys('main_pricing')
	)
	const { fieldsConfig, isLoading: isFiltersLoading } = useFilterFieldsConfig({
		sanitizeFields,
	})

	return (
		<div>
			<Header className='sticky top-0 z-10 items-center justify-between bg-background/50 px-4 py-2 backdrop-blur-md'>
				<div className='flex items-center'>
					<h2 className='items-center text-lg font-bold'>
						{t('dashboard.section.cytd_price_change.title', {
							defaultValue: 'Historical pricing results CYTD',
						})}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<InfoIcon className='ml-2 size-4' />
						</TooltipTrigger>
						<TooltipContent>
							{t('dashboard.section.cytd_price_change.tooltip', {
								defaultValue: 'dashboard.section.cytd_price_change.tooltip',
							})}
						</TooltipContent>
					</Tooltip>
				</div>
				{isFiltersLoading ? (
					<div className='flex items-center space-x-2'>
						<Skeleton className='h-6 w-28' />
						<Skeleton className='h-6 w-20' />
						<Skeleton className='h-6 w-24' />
					</div>
				) : (
					<QuickFiltersBar
						irremovableKeys={['optimization_group', 'pos_id']}
						rules={filters || []}
						storedKeys={storedKeys}
						onSaveStoredKeys={(keys) => setStoredKeys('main_pricing', keys)}
						onApply={(rules) =>
							setFilters('main_pricing', rules.map(formatFilterRule))
						}
						fieldsConfig={fieldsConfig}
						limit={7}
					/>
				)}
			</Header>
			<div className='grid grid-cols-7 gap-4 p-4'>
				<NumericOptimizedPriceWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericAcceptedPriceWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericAcceptedRateWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericRevenueBoostWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericGrossProfitBoostWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericRevenueUnderManagementWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
				<NumericBlockedForRepricingWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>
			</div>
			<div className='grid grid-cols-[1fr_1.7fr_1fr] gap-4 px-4'>
				<PriceAcceptanceDynamicsWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/>

				{/* <RepricedByPricingEngineWidget
					commonPeriods={yearOverYearPeriods}
					commonFilters={filters}
				/> */}
				<Suspense fallback={<Skeleton className='h-full w-full' />}>
					<BusinessMetricBoostsWidget
						key={`${yearOverYearPeriods?.main?.start_date}-${yearOverYearPeriods?.main?.end_date}`}
						commonPeriods={last90daysPeriods}
						commonFilters={filters}
					/>
				</Suspense>
			</div>
			<div className='grid grid-cols-2 gap-4 p-4'>
				<HistoricalPriceAcceptanceWidget
					className='col-span-1'
					commonPeriods={monthOverMonthPeriods}
					commonFilters={filters}
				/>
			</div>
		</div>
	)
}
const sanitizeFields = omitAll([
	'optimization_group_id',
	'pos_ids',
	'category_ids',
])
